import { BaseModel } from "@/models/products/base";
import { Finish } from "@/models/products/finish";
import { Stem } from "./configurator_options";

/**
 * Shade Quote models a spigot mount as this string rather than a `Stem`
 * member, because it is an attachment style, not a stem length.
 */
export const SPIGOT_STEM = "Spigot";

/**
 * FUPOF-193 — a base configured on the umbrella page.
 *
 * This is the shape `BaseSelector` hands back and `CustomizeUmbrella` turns
 * into cart lines. It intentionally mirrors `CustomUmbrella["base"]` field for
 * field, because that is what `/prices/base`, `/products/sku` and the cart's
 * `hasStructuredBase` check all expect — with two differences worth stating:
 *
 * - `base` and `baseFinish` are the **populated documents**, not codes. The
 *   cart reads a line whose `item_details.base.baseFinish` is a bare handle as
 *   SKU-reconstructed and stamps it "Added in NetSuite".
 * - `baseStem` may hold the `"Spigot"` sentinel, which is not a `Stem`. It is
 *   stored on the line but must be nulled out of any pricing payload.
 */
export interface BaseSelection {
  base: BaseModel;
  baseFinish: Finish;
  baseStem: Stem | typeof SPIGOT_STEM | null;
  baseOptions: BaseSelectionOptions;
}

/**
 * Keys are present only when chosen, matching what Shade Designer writes and
 * what the pricing endpoint expects — `{}` rather than `{ wheels: false }`.
 */
export interface BaseSelectionOptions {
  wheels?: boolean;
  topCover?: BaseTopCoverSelection;
}

export interface BaseTopCoverSelection {
  /** The accessory's catalog model, with no finish suffix (e.g. "36G-SQ-TC"). */
  model: string;
  display_name: string;
  finish: Finish;
}

/**
 * What `BaseSelector` emits on every change.
 *
 * Both prices are **per unit** and both are net of the price surcharge, so
 * they are directly comparable to the umbrella's `itemPrice` — but they get
 * there by different routes, which is worth knowing before changing either:
 *
 * - `basePrice` comes from `/prices/base`, which adds the surcharge; the
 *   service divides it back out (`removeSurcharge`).
 * - `coverPrice` is a catalog finish upcharge read straight off the product
 *   document, and those are stored net already. Verified: 36G-SQx3's doc says
 *   `msrp: 3810` while `/prices/base` answers `3962.4` — exactly ×1.04. So the
 *   upcharge must NOT have `removeSurcharge` applied, and `AccessoryMenu` reads
 *   it raw for the same reason.
 */
export interface BaseSelectionChange {
  /** null means the rep chose "No Base"; no base or cover line is created. */
  selection: BaseSelection | null;
  basePrice: number;
  /** The top cover's finish upcharge, or 0 when no cover is selected. */
  coverPrice: number;
  /**
   * FUPOF-193: this emit is the card settling the `preselect` it was given —
   * the reply to that preselect's own pricing, and nothing the rep did. Absent
   * on every other emit. The umbrella page takes a settled preselect as its
   * baseline, so opening a design and saving it untouched changes nothing.
   */
  fromPreselect?: boolean;
}
